// PLAN-judgment-cockpit Phase G (2026-05-15)
// SharedDetailPanel に組み込む feedback panel + Linked Observations セクション。
//
// - Entry worker の GET /api/entries/:id envelope から feedback_summary / decision_hash を取得
// - Linked Observations は GET /api/entries/search?source_article_id=... で取得 (Plan B Phase 0.5)
// - Inline feedback actions (version scope は decision_hash 必須、lineage scope は任意)
// - Retract は feedback list 上で個別に発火 (long-press / 削除 button)

import { useCallback, useEffect, useState } from 'react'
import { apiFetch } from '../../api/client'
import {
  listFeedback, retractFeedback, createLinkedObservation,
  type FeedbackItem, type FeedbackSummary,
  FEEDBACK_TYPE_LABELS,
} from '../../api/feedback'
import { searchLinkedObservations } from '../../api/kdb'
import type { SearchHit } from '../../types'
import { JudgmentFeedbackActions } from './JudgmentFeedbackActions'
import { useToast } from './common/Toast'
import { useSelectedItem } from '../hooks/useSelectedItem'

interface Props { articleId: string }

interface ArticleEnvelope {
  feedback_summary?: FeedbackSummary
  decision_hash?: string
  applicability?: string
  has_decision?: number
  decision_confidence?: 'high'|'medium'|'low'
  decision_escalation?: 0|1
}

export function JudgmentFeedbackPanel({ articleId }: Props) {
  const [envelope, setEnvelope] = useState<ArticleEnvelope | null>(null)
  const [feedback, setFeedback] = useState<FeedbackItem[]>([])
  const [linkedObs, setLinkedObs] = useState<SearchHit[]>([])
  const [error, setError] = useState<string | null>(null)
  const [reloadAt, setReloadAt] = useState(0)
  const reload = useCallback(() => setReloadAt(Date.now()), [])

  useEffect(() => {
    const ctrl = new AbortController()
    setError(null)
    Promise.all([
      apiFetch<ArticleEnvelope>(`/api/entries/${encodeURIComponent(articleId)}`, { signal: ctrl.signal }),
      listFeedback(articleId, { limit: 50 }, ctrl.signal),
      searchLinkedObservations({ source_article_id: articleId, limit: 20 }, ctrl.signal),
    ])
      .then(([env, fb, lo]) => {
        setEnvelope(env)
        setFeedback(fb.items)
        setLinkedObs(lo.entries)
      })
      .catch((e) => {
        if (e?.name === 'AbortError') return
        setError(e instanceof Error ? e.message : String(e))
      })
    return () => ctrl.abort()
  }, [articleId, reloadAt])

  return (
    <div className="space-y-5 text-sm">
      <h3 className="text-xs uppercase tracking-wide text-text-subtle">Judgment feedback</h3>
      {error && <p className="text-state-warning text-xs">{error}</p>}

      {/* Inline feedback action bar */}
      <div className="border border-border-subtle rounded p-3 bg-bg-surface">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <span className="text-xs text-text-subtle">
            この判断に feedback を残す
            {!envelope?.decision_hash && <span className="ml-2 text-state-warning">(decision_hash 未取得時は version scope 無効)</span>}
          </span>
        </div>
        <JudgmentFeedbackActions
          articleId={articleId}
          decisionHash={envelope?.decision_hash ?? null}
          onWrite={reload}
        />
      </div>

      {/* Summary */}
      {envelope?.feedback_summary && (
        <FeedbackSummaryBlock summary={envelope.feedback_summary} />
      )}

      {/* History */}
      <FeedbackHistoryList
        articleId={articleId}
        items={feedback}
        onChange={reload}
      />

      {/* Linked Observations + Add Observation form */}
      <LinkedObservationsBlock hits={linkedObs} articleId={articleId} onAdd={reload} />
    </div>
  )
}

function FeedbackSummaryBlock({ summary }: { summary: FeedbackSummary }) {
  const v = summary.version
  const l = summary.lineage
  const lo = summary.linked_observations
  return (
    <div className="border border-border-subtle rounded p-3 bg-bg-surface">
      <div className="text-xs text-text-subtle mb-2">Summary</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
        <Item label="like" value={v.like_count} />
        <Item label="concern" value={v.concern_count} />
        <Item label="dislike" value={v.dislike_count} />
        <Item label="comment" value={v.comment_count} />
        <Item label="reuse" value={l.reuse_state ?? '—'} />
        <Item label="observations" value={lo.count} />
      </div>
      <p className="text-[10px] text-text-subtle mt-2">
        version は現 decision_hash に紐づく feedback、lineage は版を跨いで継承。observations は source_article_ids 逆引き。
      </p>
    </div>
  )
}

function Item({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div className="text-text-subtle">{label}</div>
      <div className="text-base text-text-base font-semibold">{value}</div>
    </div>
  )
}

function FeedbackHistoryList({ articleId, items, onChange }: { articleId: string; items: FeedbackItem[]; onChange: () => void }) {
  const { show } = useToast()
  const [retractingId, setRetractingId] = useState<string | null>(null)

  const doRetract = async (fid: string) => {
    if (!confirm('この feedback を retract (論理削除) しますか?')) return
    setRetractingId(fid)
    try {
      await retractFeedback(articleId, fid, 'user retract from cockpit')
      show('Retracted', { kind: 'success' })
      onChange()
    } catch (e: unknown) {
      show(`retract 失敗: ${e instanceof Error ? e.message : String(e)}`, { kind: 'error' })
    } finally {
      setRetractingId(null)
    }
  }

  return (
    <div>
      <div className="text-xs text-text-subtle mb-2">Feedback history ({items.length})</div>
      {items.length === 0 ? (
        <p className="text-xs text-text-subtle italic">まだ feedback はありません</p>
      ) : (
        <ul className="space-y-1">
          {items.map((f) => (
            <li key={f.id} className="border border-border-subtle rounded px-2 py-1.5 bg-bg-surface text-xs flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <span className="font-medium">{FEEDBACK_TYPE_LABELS[f.feedback_type]}</span>
                <span className="ml-2 text-text-subtle">[{f.feedback_scope}]</span>
                {f.reason_tag && <span className="ml-2 text-text-subtle">#{f.reason_tag}</span>}
                {f.body && <div className="text-text-base mt-0.5 truncate">{f.body}</div>}
                <div className="text-[10px] text-text-subtle mt-0.5">
                  {new Date(f.created_at).toISOString().slice(0, 16).replace('T', ' ')} · by {f.created_by}
                </div>
              </div>
              <button
                type="button"
                onClick={() => doRetract(f.id)}
                disabled={retractingId === f.id}
                title="retract (論理削除)"
                className="text-[10px] px-2 py-0.5 rounded border border-border-subtle hover:bg-bg-overlay disabled:opacity-40"
              >
                {retractingId === f.id ? '…' : '×'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function LinkedObservationsBlock({ hits, articleId, onAdd }: { hits: SearchHit[]; articleId: string; onAdd: () => void }) {
  const { setSelectedItem } = useSelectedItem()
  const { show } = useToast()
  const [formOpen, setFormOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [oneLine, setOneLine] = useState('')
  const [subjectivity, setSubjectivity] = useState<'objective' | 'interpreted' | 'subjective'>('objective')
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    if (!title.trim() || !content.trim()) {
      show('title と content は必須です', { kind: 'error' })
      return
    }
    setSaving(true)
    try {
      await createLinkedObservation(articleId, {
        title: title.trim(),
        content: content.trim(),
        one_line: oneLine.trim() || undefined,
        subjectivity,
      })
      show('観察を記録しました', { kind: 'success' })
      setTitle(''); setContent(''); setOneLine(''); setSubjectivity('objective')
      setFormOpen(false)
      onAdd()
    } catch (e: unknown) {
      show(`記録失敗: ${e instanceof Error ? e.message : String(e)}`, { kind: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="text-xs text-text-subtle mb-2 flex items-center justify-between gap-2">
        <span>
          Linked Observations ({hits.length}) <span className="text-[10px]">— `source_article_ids` 逆引き</span>
        </span>
        {!formOpen && (
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="text-[10px] px-2 py-0.5 rounded border border-accent-primary/40 text-accent-primary hover:bg-accent-primary/10 transition"
            title="この Article への観察 (outcome) を Entry として記録"
          >
            ＋ 観察を追加
          </button>
        )}
      </div>

      {/* Add form */}
      {formOpen && (
        <div className="border border-accent-primary/30 rounded p-2 mb-2 bg-bg-surface space-y-2">
          <input
            type="text"
            placeholder="タイトル (必須) — 例: 3 ヶ月後の株価 -15%"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={saving}
            className="w-full text-xs px-2 py-1 bg-bg-base border border-border-subtle rounded"
          />
          <input
            type="text"
            placeholder="one_line (任意) — 1 行サマリ"
            value={oneLine}
            onChange={(e) => setOneLine(e.target.value)}
            disabled={saving}
            className="w-full text-xs px-2 py-1 bg-bg-base border border-border-subtle rounded"
          />
          <textarea
            rows={3}
            placeholder="content (必須) — 事実観察の詳細"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={saving}
            className="w-full text-xs px-2 py-1 bg-bg-base border border-border-subtle rounded resize-y"
          />
          <div className="flex items-center gap-2 text-xs">
            <label className="text-text-subtle">subjectivity:</label>
            <select
              value={subjectivity}
              onChange={(e) => setSubjectivity(e.target.value as 'objective' | 'interpreted' | 'subjective')}
              disabled={saving}
              className="bg-bg-base border border-border-subtle rounded px-1.5 py-0.5"
            >
              <option value="objective">objective (事実)</option>
              <option value="interpreted">interpreted (解釈)</option>
              <option value="subjective">subjective (主観)</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void submit()}
              disabled={saving || !title.trim() || !content.trim()}
              className="text-xs px-3 py-1 rounded bg-accent-primary text-bg-surface hover:opacity-90 disabled:opacity-40 transition"
            >
              {saving ? '保存中…' : '記録'}
            </button>
            <button
              type="button"
              onClick={() => setFormOpen(false)}
              disabled={saving}
              className="text-xs px-3 py-1 rounded border border-border-subtle text-text-subtle hover:bg-bg-overlay disabled:opacity-40"
            >
              キャンセル
            </button>
          </div>
          <p className="text-[10px] text-text-subtle">
            outcome (事後事実) は Entry として記録され、`source_article_ids` で本 Article と linkage されます。
            kdb-recall (entry_search) からも参照可能、PVW-DSL の decisive_O 経路にも乗ります。
          </p>
        </div>
      )}

      {hits.length === 0 ? (
        <p className="text-xs text-text-subtle italic">
          観察 Entry はまだありません。事後の事実 (outcome) を記録するときは Entry を作成し `source_article_ids` に本 Article を含めてください。
        </p>
      ) : (
        <ul className="space-y-1">
          {hits.map((h) => {
            const itemType: 'entry' | 'article' = h.origin === 'synthesized' ? 'article' : 'entry'
            return (
              <li
                key={h.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedItem({ id: h.id, itemType })}
                onKeyDown={(e) => { if (e.key === 'Enter') setSelectedItem({ id: h.id, itemType }) }}
                className="border border-border-subtle rounded px-2 py-1.5 bg-bg-surface text-xs cursor-pointer hover:bg-bg-overlay transition"
              >
                <div className="font-medium truncate">{h.title}</div>
                <div className="text-[10px] text-text-subtle mt-0.5">
                  {new Date(h.created_at).toISOString().slice(0, 10)} · {h.origin} / {h.subjectivity}
                  {h.source_project && <span> · {h.source_project}</span>}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
